import { ActivateProgressSteps as ProgressSteps } from 'src/widgets/ActivateProgressSteps';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from 'src/lib/utils';
import { EscrowStatusEnum, type ISoftwareEscrow } from 'src/graphql/schema';
import { OtherEscrowStatusEnum } from 'src/shared/constants';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { setTrackToStatus, getEscrowStatus } from 'src/utils/escrow';

const steps = [
  {
    value: 'setup' as const,
    label: 'Setup',
  },
  {
    value: 'review' as const,
    label: 'Review',
  },
  {
    value: 'signature' as const,
    label: 'Signature',
  },
  {
    value: 'activation' as const,
    label: 'Activation',
  },
];

const statusToActiveStep = {
  [EscrowStatusEnum.AdminReview]: 'setup',
  [EscrowStatusEnum.Onboarding]: 'review',
  [EscrowStatusEnum.Signed]: 'activation',
  [OtherEscrowStatusEnum.ActivationProcess]: 'activation',
  [EscrowStatusEnum.Terminated]: 'activation',
  [OtherEscrowStatusEnum.AwaitingSignature]: 'signature',
} as const;

type Props = {
  className?: string;
};

const ActivateProgressSteps = ({ className }: Props) => {
  const {
    softwareEscrow,
    softwareEscrow: { status: escrowStatus, agreement, escrowTermination, pendingActivation },
  } = useSoftwareEscrow();

  const status = getEscrowStatus({
    escrowStatus,
    escrowAgreementApproved: Boolean(agreement?.approved),
    escrowTermination,
    pendingActivation,
  });

  const previousStatus = getPreviousStatus(status, softwareEscrow);

  return (
    <Card
      className={cn(
        'flex flex-col bg-brand-foreground',
        status === EscrowStatusEnum.Terminated && 'opacity-70 pointer-events-none',
        className,
      )}
    >
      <CardHeader className="shrink-0">
        <CardTitle className="text-lg">Steps to activate your escrow</CardTitle>
        <CardDescription>Your escrow is in progress. Follow the steps to activate it!</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col justify-center grow">
        <ProgressSteps
          steps={steps}
          activeStep={
            statusToActiveStep[(previousStatus ? previousStatus : status) as keyof typeof statusToActiveStep] ?? 'setup'
          }
        />
      </CardContent>
    </Card>
  );
};

function getPreviousStatus(status: EscrowStatusEnum | OtherEscrowStatusEnum, softwareEscrow: ISoftwareEscrow) {
  const previousStatusMap = {
    [OtherEscrowStatusEnum.TerminationRequested]: softwareEscrow.status,
    [EscrowStatusEnum.PendingTermination]: setTrackToStatus({
      createdAt: softwareEscrow.createdAt,
      adminReviewAt: softwareEscrow.adminReviewAt,
      onboardingAt: softwareEscrow.onboardingAt,
      signedAt: softwareEscrow.signedAt,
      activatedAt: softwareEscrow.activatedAt,
    }),
  };

  return previousStatusMap[status as keyof typeof previousStatusMap] ?? null;
}

export default ActivateProgressSteps;
